import { createTheme as createMuiTheme, Theme } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { nlNL } from '@mui/material/locale';
import { nlNL as datenlNL } from '@mui/x-date-pickers/locales';

import { IThemeFragment } from 'graphql/types';

import { createPaletteSetup } from './palette';
import { createTypography } from './createTypography';

export const createTheme = (theme?: IThemeFragment): Theme =>
    createMuiTheme(
        {
            cssVariables: true,
            typography: createTypography(theme),
            palette: createPaletteSetup(theme?.palette),
            shape: {
                borderRadius: 5,
            },
            breakpoints: {
                values: {
                    xs: 0,
                    sm: 600,
                    md: 960,
                    lg: 1280,
                    xl: 1920,
                },
            },
            mixins: {
                toolbar: {
                    height: 80,
                },
                contextActionBar: {
                    height: 49,
                },
                ghostUserBar: {
                    height: 36,
                },
            },
            zIndex: {
                dropdown: 1075,
                fileUpload: 1250,
            },
            components: {
                MuiButton: {
                    defaultProps: {
                        disableElevation: true,
                        color: 'inherit',
                    },
                    styleOverrides: {
                        root: {
                            textTransform: 'none',
                            fontWeight: 600,
                        },
                        outlined: ({ theme }) =>
                            theme.unstable_sx({
                                borderColor: 'grey.300',
                                color: 'text.primary',
                                backgroundColor: 'common.white',
                                '&:hover': {
                                    backgroundColor: 'grey.100',
                                },
                                '&.Mui-disabled': {
                                    color: 'grey.600',
                                    backgroundColor: 'grey.100',
                                },
                            }),
                        text: ({ theme }) => ({
                            '&:hover': {
                                backgroundColor: 'transparent',
                                color: theme.palette.primary.main,
                            },
                        }),
                        sizeSmall: ({ theme }) =>
                            theme.unstable_sx({
                                height: 30,
                                px: 1.25,
                            }),
                    },
                },
                MuiButtonBase: {
                    defaultProps: {
                        disableRipple: true,
                    },
                },
                MuiTab: {
                    defaultProps: {
                        disableRipple: true,
                        tabIndex: 0,
                    },
                    styleOverrides: {
                        root: {
                            '&:focus-visible': {
                                outlineOffset: '-2px',
                            },
                        },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        sizeMedium: {
                            // Override chip font-size to change it in the multiple select input
                            fontSize: '1.2rem',
                            height: 28,
                            lineHeight: 1,
                        },
                    },
                },
                MuiInputBase: {
                    styleOverrides: {
                        root: {
                            // Override font-size to change it in the InputBase components used in e.g. SearchBar
                            fontSize: '1.6rem',
                        },
                    },
                },
                MuiInputLabel: {
                    styleOverrides: {
                        shrink: {
                            // Set shrink font-size to fix issues for label with fontSize from input being changed.
                            fontSize: '1.6rem',
                        },
                    },
                },
                MuiPickersInputBase: {
                    styleOverrides: {
                        root: {
                            fontSize: '1.6rem',
                            lineHeight: '1.4375em',
                        },
                    },
                },
                MuiTable: {
                    styleOverrides: {
                        root: {
                            borderCollapse: 'unset',
                        },
                    },
                },
                MuiMenuItem: {
                    styleOverrides: {
                        root: ({ theme }) =>
                            theme.unstable_sx({
                                minHeight: 0,
                                py: 0,
                                pr: '26px',
                                pl: 2,
                                fontSize: '1.6rem',
                            }),
                    },
                },
                MuiTableContainer: {
                    styleOverrides: {
                        root: {
                            position: 'relative',
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            '&:focus-visible': {
                                outlineOffset: '-3px',
                            },
                        },
                    },
                },
                MuiPaginationItem: {
                    styleOverrides: {
                        root: {
                            '&:focus-visible': {
                                outlineOffset: '-2px',
                            },
                        },
                    },
                },
                MuiListItemButton: {
                    styleOverrides: {
                        root: {
                            '&:focus-visible': {
                                outlineOffset: '-2px',
                            },
                        },
                    },
                },
                MuiCardActionArea: {
                    styleOverrides: {
                        root: {
                            '&:focus-visible': {
                                outlineOffset: '-2px',
                            },
                        },
                    },
                },
                MuiToggleButton: {
                    styleOverrides: {
                        root: ({ theme }) =>
                            theme.unstable_sx({
                                textTransform: 'none',
                                color: 'text.primary',
                                fontSize: '1.3rem',
                                fontWeight: 600,
                            }),
                        sizeSmall: ({ theme }) =>
                            theme.unstable_sx({
                                px: 1.25,
                                py: 0.75,
                            }),
                    },
                },
            },
        },
        datenlNL,
        nlNL
    );
